import { ReactElement, useEffect, useRef, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Search } from '@novaheal/types';
import { useSearchHistoryEvents, useAutocomplete } from 'hooks/api/backend/search/search';
import { Hit } from 'instantsearch.js';
import { IconType } from 'types/iconType';
import { useAppContext } from 'store/AppContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { ProtectedRoutes } from 'types/Routes';
import { Z_INDEX } from 'styles/zIndex';
import { SearchInput } from './SearchInput';
import { SearchDropdownList } from './SearchDropdownList/SearchDropdownList';
import { useBreakpoint } from '../../hooks/utils/layout/useBreakpoint';
import { Breakpoints } from '../../types/breakpoints';

export const SearchBar = (): ReactElement => {
  const { setDropdownOpen } = useAppContext();
  const { data: searchHistory } = useSearchHistoryEvents();
  const { indices, currentRefinement } = useAutocomplete();
  const [dropdownIsOpen, setDropdownIsOpen] = useState(false);

  const isDesktop = useBreakpoint(Breakpoints.DESKTOP);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const showCancelButton = !isDesktop && (dropdownIsOpen || currentRefinement !== '');
  const hasHistoryResults = !!searchHistory && searchHistory.length > 0;
  const showHistory = hasHistoryResults && currentRefinement.length <= 1;

  const containerBg = isDesktop ? undefined : 'background.accent';
  const containerMaxW = '1120px';
  const containerPaddingX = isDesktop ? '80px' : '0';
  const containerMargin = isDesktop ? '0 auto' : '0 !important';

  // Update dropdown state in store to tell the mobile layout to hide the topbar
  useEffect(() => {
    setDropdownOpen(dropdownIsOpen);
  }, [dropdownIsOpen, setDropdownOpen]);

  // Hide dropdown listeners
  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        inputRef.current?.blur();
      }
    };

    document.addEventListener('keydown', handleKeydown);

    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, []);

  const handleFocus = (): void => {
    setDropdownIsOpen(true);
  };

  const handleBlur = (): void => {
    setDropdownIsOpen(false);
  };

  const handleCancelClick = (): void => {
    inputRef.current?.blur();
  };

  const navigateToSearchPage = (searchQuery?: string): void => {
    const queryParam = searchQuery || inputRef.current?.value.trim();

    // Get current "t" param from URL if present
    const urlParams = new URLSearchParams(location.search);
    const tParam = urlParams.get('t');
    let searchUrl = `${ProtectedRoutes.SEARCH}`;

    const params = new URLSearchParams();
    if (queryParam && queryParam.trim()) {
      params.set('q', queryParam.trim());
    }
    if (tParam) {
      params.set('t', tParam);
    }
    if (Array.from(params).length > 0) {
      searchUrl += `?${params.toString()}`;
    }

    navigate(searchUrl);
  };

  const handleSeeMoreClick = (): void => {
    inputRef.current?.blur();
    navigateToSearchPage();
  };

  const handleSubmit = (): void => {
    inputRef.current?.blur();
    navigateToSearchPage();
  };

  const searchRecommendations = indices[0].hits.map((hit: Hit) => ({
    text: hit.fields.title || hit.fields.name,
    id: hit.objectID,
    contentType: hit.contentType,
  }));

  const dropdownData = showHistory
    ? {
        items: searchHistory.map((item: Search) => ({
          text: item.searchTerm,
          id: item.id,
        })),
        variant: IconType.HISTORY,
      }
    : {
        items: searchRecommendations,
        variant: IconType.RECOMMEND,
      };

  const dropdownItemFn = async (id: string, text: string): Promise<void> => {
    if (inputRef.current) {
      // get and set the current value within react context
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value'
      )?.set;
      nativeInputValueSetter?.call(inputRef.current, text);

      // fire a new event to trigger the change as if the value change was native
      const event = new Event('input', { bubbles: true });
      inputRef.current.dispatchEvent(event);
    }
    navigateToSearchPage(text);
  };

  return (
    <Flex
      justifyContent="space-between"
      flexDirection="column"
      bg={containerBg}
      width="100%"
      maxW={containerMaxW}
      paddingX={containerPaddingX}
      zIndex={Z_INDEX.SEARCH_BAR}
      margin={containerMargin}
    >
      <Box p={3} width="100%">
        <Box width="100%" position="relative">
          <SearchInput
            ref={inputRef}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onCancelClick={handleCancelClick}
            showCancelButton={showCancelButton}
            onSubmit={handleSubmit}
          />
          <SearchDropdownList
            isOpen={dropdownIsOpen}
            listItems={dropdownData.items}
            variant={dropdownData.variant}
            onItemClick={dropdownItemFn}
            onSeeMoreClick={handleSeeMoreClick}
          />
        </Box>
      </Box>
    </Flex>
  );
};
